// Folding a game together.
//
// A Cartographers game is fully described by two things: the seed (which
// fixes the entire card script, see script.js) and the placements everyone
// has drawn. This module folds those into the view the UI and bots read:
// every map, whose drawing we are waiting on, season scores, totals.
//
// A placement is { cells: [[r, c], ...], terrain, fallback } - `fallback`
// marks the 1x1 escape hatch (rules p6/p7) rather than one of the card's
// shapes. Placements are validated strictly and deterministically here, so
// every client folding the same inputs sees the identical game; an invalid
// record draws nothing rather than corrupting a map.

import { buildScript } from './script';
import {
  emptyGrid, draw, sameShape, anyPlacement, fitsAt,
} from './board';
import { cardById } from './cards';
import { scoreEdict, monsterPenalty } from './scoring';

// The score sheet's coin track has 14 boxes; there is nowhere to record a
// fifteenth coin, so 14 is the cap (sheet, and the mod's own score pad).
export const MAX_COINS = 14;

// The cardboard game plays any number ("100 MAP SHEETS"); the digital table
// is 2-6 because ambushes here always use the multiplayer pass-the-map rule,
// which needs a neighbour - a lone human plays against at least one bot, the
// house arrangement in every sibling game anyway.
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;

const scriptCache = new Map();
export const scriptFor = (seed) => {
  if (!scriptCache.has(seed)) scriptCache.set(seed, buildScript(seed));
  return scriptCache.get(seed);
};

// Who draws on this map this turn. Explore turns: your own map. Ambush turns:
// map sheets are passed to the neighbour in the arrow's direction (rules p7),
// so the drawer of map m is the seat the map lands in front of. Seats are the
// table read clockwise.
export const drawerOfMap = (turn, mapSeat, playerCount) => {
  if (turn.type !== 'ambush' || playerCount < 2) return mapSeat;
  const offset = turn.direction === 'clockwise' ? 1 : playerCount - 1;
  return (mapSeat + offset) % playerCount;
};

export const mapDrawnBy = (turn, seat, playerCount) => {
  if (turn.type !== 'ambush' || playerCount < 2) return seat;
  const offset = turn.direction === 'clockwise' ? 1 : playerCount - 1;
  return (seat - offset + playerCount) % playerCount;
};

// The drawing choices a turn offers on a given grid. Each option is
// { cells, terrains, coin, fallback }. When none of the card's shapes fit
// (under the ruins constraint if one is active), the fallback 1x1 becomes the
// only option - any terrain after a blocked ruins card (rules p7), the card's
// own terrains otherwise (p6). Ambushes offer the monster shape, or a 1x1
// monster when it cannot fit (p7).
export const optionsForTurn = (turn, grid) => {
  const card = cardById(turn.cardId);
  if (turn.type === 'ambush') {
    const shapes = [{ cells: card.cells, terrains: ['monster'] }];
    if (anyPlacement(grid, shapes, { needsRuin: false })) {
      return shapes.map((s) => ({ ...s, coin: false, fallback: false }));
    }
    return [{
      cells: [[0, 0]], terrains: ['monster'], coin: false, fallback: true,
    }];
  }
  const needsRuin = Boolean(turn.ruins);
  if (anyPlacement(grid, card.shapes, { needsRuin })) {
    return card.shapes.map((s) => ({
      cells: s.cells, terrains: s.terrains, coin: Boolean(s.coin), fallback: false,
    }));
  }
  const terrains = needsRuin
    ? ['forest', 'village', 'farm', 'water', 'monster']
    : [...new Set(card.shapes.flatMap((s) => s.terrains))];
  return [{
    cells: [[0, 0]], terrains, coin: false, fallback: true,
  }];
};

// Strict legality of a stored placement against the grid it lands on.
export const validatePlacement = (grid, turn, placement) => {
  if (!placement || !Array.isArray(placement.cells) || !placement.cells.length) return false;
  const options = optionsForTurn(turn, grid);
  const option = options.find((o) => Boolean(o.fallback) === Boolean(placement.fallback)
    && (o.fallback || sameShape(o.cells, placement.cells))
    && o.terrains.includes(placement.terrain));
  if (!option) return false;
  if (option.fallback) {
    return placement.cells.length === 1 && fitsAt(grid, placement.cells);
  }
  // The ruins constraint binds real shapes but not the fallback square
  // ("draw a 1x1 square ANYWHERE", rules p7).
  return fitsAt(grid, placement.cells, { needsRuin: Boolean(turn.ruins && turn.type === 'explore') });
};

// Whether choosing these cells earns the card's coin (rules p6).
const earnsCoin = (turn, placement) => {
  if (turn.type !== 'explore' || placement.fallback) return false;
  const card = cardById(turn.cardId);
  return (card.shapes || []).some((s) => s.coin && sameShape(s.cells, placement.cells));
};

const scoreSeason = (script, season, maps) => maps.map((map) => {
  const parts = season.edicts.map((letter) => ({
    letter,
    key: script.edicts[letter],
    stars: scoreEdict(script.edicts[letter], map.grid),
  }));
  const coins = map.coins;
  const monsters = monsterPenalty(map.grid);
  return {
    edicts: parts,
    coins,
    monsters,
    total: parts.reduce((sum, p) => sum + p.stars, 0) + coins - monsters,
  };
});

/**
 * The fold. `placements` is { [turnIndex]: { [mapSeat]: placement } } (values
 * may arrive JSON-stringified from the wire; decoded here).
 *
 * Placements already made for the current turn are applied immediately - your
 * own drawing should not wait for the table - but the turn advances only when
 * every map has one.
 */
export const foldGame = (seed, playerCount, placements) => {
  const script = scriptFor(seed);
  const maps = Array.from({ length: playerCount }, () => ({ grid: emptyGrid(), coins: 0 }));
  const seasonScores = [];

  let turnIndex = script.turns.length;
  for (let t = 0; t < script.turns.length; t += 1) {
    const turn = script.turns[t];
    const byMap = placements?.[t] || {};
    let complete = true;
    for (let m = 0; m < playerCount; m += 1) {
      let placement = byMap[m];
      if (typeof placement === 'string') {
        try { placement = JSON.parse(placement); } catch { placement = null; }
      }
      if (!placement) { complete = false; continue; } // eslint-disable-line no-continue
      if (!validatePlacement(maps[m].grid, turn, placement)) continue; // eslint-disable-line no-continue
      const [grid, mountainCoins] = draw(maps[m].grid, placement.cells, placement.terrain);
      maps[m].grid = grid;
      maps[m].coins = Math.min(MAX_COINS, maps[m].coins
        + mountainCoins + (earnsCoin(turn, placement) ? 1 : 0));
    }
    if (!complete) { turnIndex = t; break; }
    const season = script.seasons[turn.season];
    if (t === season.firstTurn + season.turnCount - 1) {
      seasonScores.push(scoreSeason(script, season, maps));
    }
  }

  const over = turnIndex === script.turns.length;
  const totals = maps.map((_, seat) => seasonScores
    .reduce((sum, scores) => sum + scores[seat].total, 0));

  return {
    script,
    maps,
    turnIndex,
    turn: over ? null : script.turns[turnIndex],
    phase: over ? 'over' : 'playing',
    seasonScores,
    totals,
  };
};

// Map seats still missing a drawing for the current turn.
export const waitingMaps = (view, placements) => {
  if (view.phase === 'over') return [];
  const byMap = placements?.[view.turnIndex] || {};
  return view.maps.map((_, m) => m).filter((m) => !byMap[m]);
};
